import { useLoaderData, useRevalidator, useRouteError } from "react-router-dom";
import GenericErrorDisplay, { useSetError } from "../generic-error";
import TransmedProcessService from "../../service/transmed/process";
import OutputTransmedEdition, {
  TransmedEditionSeason,
} from "../../dtos/transmed/process/edition";
import NumberInput from "../../components/inputs/number";
import RadioInput, { RadioInputGroup } from "../../components/inputs/radio";
import TextInput from "../../components/inputs/text";
import DateTimeRangeInput from "../../components/inputs/datetime/range";
import DateTimeInput from "../../components/inputs/datetime/datetime";
import FileInput from "../../components/inputs/file";
import { useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import { DateRange } from "@mui/x-date-pickers-pro/models";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
import { CircularProgress } from "@mui/material";
import DateInput from "../../components/inputs/date/datetime";
import DateRangeInput from "../../components/inputs/date/range";
import InputDialog from "../../components/dialog";

export const path = "transmed/edition";

export async function loader() {
  return await new TransmedProcessService().read.edition();
}

export function Component() {
  const revalidator = useRevalidator();
  const service = new TransmedProcessService();
  const edition = useLoaderData() as OutputTransmedEdition;

  const editable = () =>
    !edition.process_started ||
    edition.participation_proof_deadline < new Date();

  const [year, setYear] = useState<number | null>(edition.year);
  const [season, setSeason] = useState<TransmedEditionSeason>(edition.season);
  const [periodText, setPeriodText] = useState<string>(edition.period_text);
  const [registrationPeriod, setRegistrationPeriod] = useState<
    DateRange<Dayjs>
  >([dayjs(edition.registration_start), dayjs(edition.registration_end)]);
  const [maxPoints, setMaxPoints] = useState<number | null>(edition.max_points);
  const [pointsDeadlineFirst, setPointsDeadlineFirst] = useState<Dayjs | null>(
    dayjs(edition.points_deadline_first)
  );
  const [contestingDeadline, setContestingDeadline] = useState<Dayjs | null>(
    dayjs(edition.contesting_deadline)
  );
  const [pointsDeadlineSecond, setPointsDeadlineSecond] =
    useState<Dayjs | null>(dayjs(edition.points_deadline_second));
  const [distributionStart, setDistributionStart] = useState<Dayjs | null>(
    dayjs(edition.distribution_start)
  );
  const [exchangePeriod, setExchangePeriod] = useState<DateRange<Dayjs>>([
    dayjs(edition.exchange_start),
    dayjs(edition.exchange_end),
  ]);
  const [participationProofDeadline, setParticipationProofDeadline] =
    useState<Dayjs | null>(dayjs(edition.participation_proof_deadline));
  const [regulations, setRegulations] = useState<File | null>(null);
  const [participationProof, setParticipationProof] = useState<File | null>(
    null
  );

  const setErrorOnly = useSetError();
  const setError = (error: unknown) => {
    setErrorOnly(error);
    revalidator.revalidate();
  };
  const [icon, setIcon] = useState<JSX.Element | null>(null);
  const [dialogCallback, setDialogCallback] = useState<
    (() => Promise<void> | void) | null
  >(null);

  return (
    <form
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        justifyContent: "space-evenly",
        gap: "var(--gap-medium)",
      }}
      onSubmit={(e) => {
        e.preventDefault();
        setError("");
        setIcon(<CircularProgress />);

        if (year === null) {
          setError("Year cannot be empty.");
          return;
        }
        if (registrationPeriod[0] === null || registrationPeriod[1] === null) {
          setError("Registration period must be fully completed.");
          return;
        }
        if (maxPoints === null) {
          setError("Max points cannot be empty.");
          return;
        }
        if (pointsDeadlineFirst === null) {
          setError("Deadline for first points cannot be empty.");
          return;
        }
        if (contestingDeadline === null) {
          setError("Contestations deadline cannot be empty.");
          return;
        }
        if (pointsDeadlineSecond === null) {
          setError("Deadline for second points cannot be empty.");
          return;
        }
        if (distributionStart === null) {
          setError("Start date of distribution cannot be empty.");
          return;
        }
        if (exchangePeriod[0] === null || exchangePeriod[1] === null) {
          setError("Registration period must be fully completed.");
          return;
        }
        if (participationProofDeadline === null) {
          setError("Deadline for participation proof upload cannot be empty.");
          return;
        }

        service.update
          .edition(
            year,
            season,
            periodText,
            registrationPeriod[0].toDate(),
            registrationPeriod[1].toDate(),
            maxPoints,
            pointsDeadlineFirst.toDate(),
            contestingDeadline.toDate(),
            pointsDeadlineSecond.toDate(),
            distributionStart.toDate(),
            exchangePeriod[0].toDate(),
            exchangePeriod[1].toDate(),
            participationProofDeadline.toDate(),
            regulations,
            participationProof
          )
          .then(async () => {
            revalidator.revalidate();
            setIcon(<FontAwesomeIcon icon={faCheck} />);
          })
          .catch((error) => {
            setIcon(null);
            setError(error);
          });
      }}
    >
      <h1>Ediția curentă</h1>
      {!editable() && (
        <span style={{ color: "red" }}>
          Nu poți modifica ediția curentă în timp ce este în desfășurare!
        </span>
      )}
      <NumberInput
        style={{ flex: 1 }}
        label="Anul"
        disabled={!editable()}
        value={year}
        onChange={(event) =>
          setYear(
            isNaN(parseInt(event.target.value))
              ? null
              : parseInt(event.target.value)
          )
        }
        required
      />
      <RadioInputGroup
        style={{ flex: 1 }}
        label="Sezonul"
        value={season}
        onChange={(event) =>
          setSeason(
            TransmedEditionSeason[
              event.target.value as keyof typeof TransmedEditionSeason
            ]
          )
        }
      >
        <RadioInput
          label="Toamnă-Iarnă"
          value={TransmedEditionSeason.autumn}
          disabled={!editable()}
        />
        <RadioInput
          label="Primăvară"
          value={TransmedEditionSeason.spring}
          disabled={!editable()}
        />
      </RadioInputGroup>
      <TextInput
        style={{ flex: 1 }}
        label="Perioada în cuvinte"
        disabled={!editable()}
        value={periodText}
        onChange={(event) => setPeriodText(event.target.value)}
        required
      />
      <DateTimeRangeInput
        className="responsive-range-input"
        label="Perioada de înscrieri"
        disabled={!editable()}
        value={registrationPeriod}
        onChange={(value) => setRegistrationPeriod(value)}
      />
      <NumberInput
        style={{ flex: 1 }}
        label="Punctaj maxim"
        disabled={!editable()}
        value={maxPoints}
        onChange={(event) =>
          setMaxPoints(
            isNaN(parseInt(event.target.value))
              ? null
              : parseInt(event.target.value)
          )
        }
        required
      />
      <DateTimeInput
        sx={{ flex: 1 }}
        label="Data afișării rezultatelor inițiale"
        disabled={!editable()}
        value={pointsDeadlineFirst}
        onChange={(value) => setPointsDeadlineFirst(value)}
      />
      <DateTimeInput
        sx={{ flex: 1 }}
        label="Termen contestații"
        disabled={!editable()}
        value={contestingDeadline}
        onChange={(value) => setContestingDeadline(value)}
      />
      <DateTimeInput
        sx={{ flex: 1 }}
        label="Data afișării rezultatelor finale"
        disabled={!editable()}
        value={pointsDeadlineSecond}
        onChange={(value) => setPointsDeadlineSecond(value)}
      />
      <DateInput
        sx={{ flex: 1 }}
        label="Data începerii distribuției"
        disabled={!editable()}
        value={distributionStart}
        onChange={(value) => setDistributionStart(value)}
      />
      <DateRangeInput
        className="responsive-range-input"
        label="Perioada schimbului efectiv"
        disabled={!editable()}
        value={exchangePeriod}
        onChange={(value) => setExchangePeriod(value)}
      />
      <DateTimeInput
        sx={{ flex: 1 }}
        label="Termen încărcare adeverințe"
        disabled={!editable()}
        value={participationProofDeadline}
        onChange={(value) => setParticipationProofDeadline(value)}
      />
      <FileInput
        style={{ flex: 1 }}
        label="Regulament"
        remoteValue={edition.regulations}
        disabled={!editable()}
        onChange={(files) =>
          setRegulations(files.length > 0 ? files[0] ?? null : null)
        }
        types={["pdf", "PDF"]}
      />
      <FileInput
        style={{ flex: 1 }}
        remoteValue={edition.participation_proof_template}
        label="Adeverința necompletată"
        disabled={!editable()}
        onChange={(files) =>
          setParticipationProof(files.length > 0 ? files[0] ?? null : null)
        }
        types={["pdf", "PDF"]}
      />
      {!edition.process_started && (
        <button
          type="submit"
          className="button primary outlined"
          style={{ alignSelf: "center", marginTop: "var(--gap-medium)" }}
        >
          {icon} Salvează datele
        </button>
      )}
      {new Date() < edition.participation_proof_deadline ? (
        <button
          type="button"
          className="button primary"
          style={{
            alignSelf: "center",
            margin: "var(--gap-xl) 0",
            fontSize: "var(--title-font-size)",
            fontWeight: "var(--title-font-weight)",
            padding: "var(--gap-medium) var(--gap-big)",
            ...{ "--color": editable() ? "red" : "" },
          }}
          disabled={!editable()}
          onClick={() =>
            setDialogCallback(() => async () => {
              setError("");
              setIcon(<CircularProgress />);
              try {
                await service.create.start();
                revalidator.revalidate();
                setIcon(<FontAwesomeIcon icon={faCheck} />);
              } catch (error) {
                setError(error);
                setIcon(null);
              }
            })
          }
        >
          {revalidator.state === "idle" ? (
            icon
          ) : (
            <CircularProgress sx={{ color: "inherit" }} />
          )}{" "}
          {edition.process_started
            ? "TRANSMED ÎN DESFĂȘURARE"
            : "START TRANSMED"}
        </button>
      ) : (
        <button
          type="submit"
          className="button primary"
          style={{
            alignSelf: "center",
            margin: "var(--gap-xl) 0",
            fontSize: "var(--title-font-size)",
            fontWeight: "var(--title-font-weight)",
            padding: "var(--gap-medium) var(--gap-big)",
          }}
        >
          {icon} Ediție nouă
        </button>
      )}
      <InputDialog
        title="Start Transmed"
        continue_label="Start Transmed"
        abort_label="Anulează"
        dialogCallback={dialogCallback}
        onClose={() => setDialogCallback(null)}
      >
        Atenție! Această acțiune pornește procesul de înscriere, validare,
        contestație și mai apoi distribuție din cadrul transmed. Nu vei mai
        putea aduce modificări ediției, formularului, locurilor și perioadelor
        fiecărui centru până la termenul în care participanții trebuie să își
        încarce adeverințele de participare (adică, finalizarea ediției). De
        asemenea, o dată cu începerea procesului, pe pagina de oportunități vor
        fi afișate locurile puse la dispoziție de fiecare centru.
      </InputDialog>
    </form>
  );
}
Component.displayName = "page-account-transmed-edition";

export function ErrorBoundary() {
  const error = useRouteError();
  return <GenericErrorDisplay error={error} />;
}
ErrorBoundary.displayName = "page-account-transmed-edition-error";
